import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, Wallet, CalendarClock, Hourglass, CheckCircle2, Landmark, Save, FileText, FileSpreadsheet } from 'lucide-react';
import { FinanceChart } from './FinanceChart';
import { downloadFile, todayStamp } from '../lib/download';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// PDFs need the auth header, so fetch as blob and open in a new tab
export const openReceipt = async (url, headers) => {
  const win = window.open('', '_blank');
  try {
    const r = await axios.get(url, { headers, responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    if (win) win.location.href = blobUrl; else window.location.href = blobUrl;
  } catch (e) {
    if (win) win.close();
    alert('Não foi possível abrir o comprovante.');
  }
};
export const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
export const fmtDay = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR') : '—');
const card = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1.25rem' };
const errMsg = (e, d) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : d);

const SummaryCard = ({ icon: Icon, label, value, color, testId }) => (
  <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
    <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon className="w-5 h-5" style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: '0.75rem', color: '#718096' }}>{label}</p>
      <p style={{ fontSize: '1.3rem', fontWeight: 700, color: '#2D3748' }} data-testid={testId}>{value}</p>
    </div>
  </div>
);

const PriceCard = ({ data, headers, onSaved }) => {
  const [isPublic, setIsPublic] = useState(data.plataforma_publica);
  const [valor, setValor] = useState(data.valor_assinatura ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const msg = isPublic
      ? 'Tornar a biblioteca pública? Todos os leitores cadastrados (inclusive os pendentes) passam a ler sem pagamento.'
      : data.plataforma_publica ? 'Voltar a cobrar assinatura? Leitores que entraram de graça ficarão pendentes até assinar.' : null;
    if (msg && !window.confirm(msg)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/digital/settings/subscription`, { plataforma_publica: isPublic, valor: isPublic ? null : Number(valor) }, { headers });
      alert('Assinatura atualizada!');
      onSaved();
    } catch (e) {
      alert(errMsg(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };
  const invalid = !isPublic && !(Number(valor) >= 10 && Number(valor) <= 300);
  return (
    <div style={card} data-testid="finance-price-card">
      <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>Assinatura da biblioteca</h3>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[{ v: false, l: 'Assinatura paga' }, { v: true, l: 'Plataforma pública (sem cobrança)' }].map((o) => (
          <button key={String(o.v)} type="button" onClick={() => setIsPublic(o.v)} data-testid={`finance-mode-${o.v ? 'public' : 'paid'}`}
            style={{ padding: '0.55rem 1rem', borderRadius: 8, border: `2px solid ${isPublic === o.v ? '#68AAB0' : '#e2e8f0'}`, background: isPublic === o.v ? '#e6fffa' : 'white', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>
            {o.l}
          </button>
        ))}
      </div>
      {isPublic ? (
        <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>Os leitores apenas se cadastram e já podem ler, sem pagamento.</p>
      ) : (
        <div style={{ marginBottom: '1rem', maxWidth: 260 }}>
          <Label>Valor mensal (R$ 10,00 a R$ 300,00)</Label>
          <Input type="number" min={10} max={300} step="0.01" value={valor} onChange={(e) => setValor(e.target.value)} data-testid="finance-price-input" />
          {invalid && valor !== '' && <p style={{ color: '#e53e3e', fontSize: '0.75rem', marginTop: 4 }}>O valor deve estar entre R$ 10,00 e R$ 300,00</p>}
          <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: 6 }}>Taxa da plataforma: {data.percentual_plataforma}% · você recebe {100 - data.percentual_plataforma}%</p>
        </div>
      )}
      <Button onClick={save} disabled={saving || invalid} style={{ background: '#68AAB0' }} data-testid="finance-price-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salvar
      </Button>
    </div>
  );
};

const AdultCard = ({ data, headers, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !data.adult_only;
    if (next && !window.confirm('Marcar o catálogo como +18? Novos cadastros com menos de 18 anos serão bloqueados. Quem já é assinante não é afetado.')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/digital/settings/adult`, { adult_only: next }, { headers });
      onSaved();
    } catch (e) {
      alert(errMsg(e, 'Erro ao salvar'));
    } finally { setSaving(false); }
  };
  return (
    <div style={card} data-testid="finance-adult-card">
      <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.5rem' }}>Classificação etária</h3>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: '#4a5568' }}>
        <input type="checkbox" checked={Boolean(data.adult_only)} onChange={toggle} disabled={saving} data-testid="finance-adult-toggle" style={{ width: 18, height: 18, accentColor: '#68AAB0' }} />
        Conteúdo para maiores de 18 anos
        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
      </label>
      <p style={{ fontSize: '0.78rem', color: '#718096', marginTop: 6 }}>Quando marcado, quem tiver menos de 18 anos não consegue concluir o cadastro no seu catálogo. A data de nascimento é pedida em todo novo cadastro.</p>
    </div>
  );
};

// Age rating lives on the main dashboard; loads its own flag
export const DashboardAdultCard = ({ headers }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/digital/finance`, { headers }).then((r) => setData({ adult_only: r.data.adult_only })).catch(() => {});
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  return <div style={{ marginTop: '1.5rem' }}><AdultCard data={data} headers={headers} onSaved={load} /></div>;
};

const AccountCard = ({ data, headers, onSaved }) => {
  const c = data.conta || {};
  const [form, setForm] = useState({ titular: c.titular || '', banco: c.banco || '', agencia: c.agencia || '', conta: c.conta || '', pix: c.pix || '' });
  const [day, setDay] = useState(data.payout_day);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/digital/finance/settings`, { payout_day: day, conta: form }, { headers });
      alert('Dados de recebimento salvos!');
      onSaved();
    } catch (e) {
      alert(errMsg(e, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div style={card} data-testid="finance-account-card">
      <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: 8 }}><Landmark className="w-4 h-4" />Dados para recebimento</h3>
      <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '1rem' }}>Preencha banco, agência e conta, ou uma chave PIX.</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
        <div style={{ gridColumn: '1 / -1' }}><Label>Nome vinculado à conta *</Label><Input value={form.titular} onChange={set('titular')} data-testid="finance-titular-input" /></div>
        <div><Label>Número do banco</Label><Input value={form.banco} onChange={set('banco')} placeholder="Ex.: 001" data-testid="finance-banco-input" /></div>
        <div><Label>Agência</Label><Input value={form.agencia} onChange={set('agencia')} data-testid="finance-agencia-input" /></div>
        <div><Label>Conta</Label><Input value={form.conta} onChange={set('conta')} data-testid="finance-conta-input" /></div>
        <div><Label>Chave PIX</Label><Input value={form.pix} onChange={set('pix')} data-testid="finance-pix-input" /></div>
      </div>
      <div style={{ marginTop: '1rem' }}>
        <Label>Dia do recebimento</Label>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: 6 }}>
          {[5, 15].map((d) => (
            <button key={d} type="button" onClick={() => setDay(d)} data-testid={`finance-day-${d}`}
              style={{ padding: '0.5rem 0.9rem', borderRadius: 8, border: `2px solid ${day === d ? '#68AAB0' : '#e2e8f0'}`, background: day === d ? '#e6fffa' : 'white', cursor: 'pointer', fontSize: '0.85rem' }}>
              {d}º dia útil
            </button>
          ))}
        </div>
        <p style={{ fontSize: '0.75rem', color: '#718096', marginTop: 6 }}>Cada assinatura entra no repasse do dia escolhido depois de completar 45 dias do pagamento.</p>
      </div>
      <Button onClick={save} disabled={saving || !form.titular.trim()} style={{ background: '#68AAB0', marginTop: '1rem' }} data-testid="finance-account-save">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Salvar dados
      </Button>
    </div>
  );
};

const SimpleTable = ({ cols, rows, empty, testId }) => (
  <div style={{ overflowX: 'auto' }} data-testid={testId}>
    {rows.length === 0 ? <p style={{ fontSize: '0.85rem', color: '#a0aec0' }}>{empty}</p> : (
      <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
        <thead><tr>{cols.map((c) => <th key={c.l} style={{ textAlign: 'left', padding: '0.5rem', color: '#718096', fontWeight: 600, borderBottom: '1px solid #e2e8f0' }}>{c.l}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={r.id || i}>{cols.map((c) => <td key={c.l} style={{ padding: '0.5rem', borderBottom: '1px solid #f1f5f9', color: '#2D3748' }}>{c.f(r)}</td>)}</tr>)}</tbody>
      </table>
    )}
  </div>
);

export const EditorFinance = ({ headers }) => {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try { await downloadFile(`${API}/digital/finance/export`, headers, `financeiro-${todayStamp()}.xlsx`); } catch (e) { alert('Não foi possível exportar a planilha.'); } finally { setExporting(false); }
  };
  const load = useCallback(() => {
    axios.get(`${API}/digital/finance`, { headers }).then((r) => setData(r.data)).catch(() => setData(false));
  }, []); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  if (data === null) return <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#68AAB0' }} /></div>;
  if (data === false) return <p>Erro ao carregar o financeiro.</p>;
  const s = data.summary;
  return (
    <div data-testid="editor-finance" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, color: '#2D3748' }}>Financeiro</h1>
          <p style={{ color: '#718096' }}>Balanço das assinaturas e repasses da sua biblioteca.</p>
        </div>
        <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="finance-export-btn">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileSpreadsheet className="w-4 h-4 mr-2" style={{ color: '#217346' }} />}Exportar planilha
        </Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '1rem' }}>
        <SummaryCard icon={Wallet} label="A receber (liberado)" value={brl(s.available)} color="#38a169" testId="finance-available" />
        <SummaryCard icon={Hourglass} label="Em carência (menos de 45 dias)" value={brl(s.in_grace)} color="#d69e2e" testId="finance-in-grace" />
        <SummaryCard icon={CalendarClock} label={s.next_payout_date ? `Próximo repasse · ${fmtDay(s.next_payout_date)}` : 'Próximo repasse'} value={s.next_payout_amount ? brl(s.next_payout_amount) : 'Sem valores agendados'} color="#3182ce" testId="finance-next-payout" />
        <SummaryCard icon={CheckCircle2} label="Total já repassado" value={brl(s.paid_out)} color="#805ad5" testId="finance-paid-out" />
      </div>
      {s.has_simulated && (
        <div data-testid="finance-simulated-note" style={{ ...card, background: '#fffbeb', borderColor: '#f6e05e', color: '#92400e', fontSize: '0.9rem' }}>
          Modo teste: os valores acima incluem os <strong>assinantes simulados</strong>, para você ver como o Financeiro funciona. Eles não geram cobrança nem repasse real e somem ao contratar o plano.
        </div>
      )}
      {s.overdue_amount > 0 && (
        <div data-testid="finance-overdue" style={{ ...card, background: '#fffaf0', borderColor: '#fbd38d', color: '#9c4221', fontSize: '0.9rem' }}>
          <strong>{brl(s.overdue_amount)}</strong> de repasses com data já passada ainda aguardam pagamento pela plataforma.
        </div>
      )}
      <div style={{ ...card, fontSize: '0.85rem', color: '#4a5568', display: 'flex', gap: '2rem', flexWrap: 'wrap' }} data-testid="finance-balance">
        <span>Assinaturas vendidas: <strong>{brl(s.gross)}</strong></span>
        <span>Taxa da plataforma ({data.percentual_plataforma}%): <strong>{brl(s.platform_fee)}</strong></span>
        <span>Seu líquido: <strong>{brl(s.editor_net)}</strong></span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.25rem' }}>
        <PriceCard data={data} headers={headers} onSaved={load} />
        <AccountCard data={data} headers={headers} onSaved={load} />
      </div>
      <FinanceChart headers={headers} card={card} />
      <div style={card}>
        <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>Cronograma de repasses</h3>
        <SimpleTable testId="finance-schedule" rows={data.schedule} empty="Nenhum valor aguardando repasse."
          cols={[{ l: 'Data do repasse', f: (r) => fmtDay(r.date) }, { l: 'Valor', f: (r) => brl(r.amount) }]} />
      </div>
      <div style={card}>
        <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>Assinaturas pagas</h3>
        <SimpleTable testId="finance-payments" rows={data.payments} empty="Nenhuma assinatura paga ainda."
          cols={[
            { l: 'Data', f: (r) => (r.simulated ? new Date(r.paid_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : fmtDay(r.paid_at)) },
            { l: 'Leitor', f: (r) => (r.simulated ? <span>{r.nome} <span data-testid="finance-simulated-tag" style={{ background: '#fef3c7', color: '#92400e', borderRadius: 999, padding: '0 0.5rem', fontSize: '0.75rem', fontWeight: 600 }}>Simulação</span></span> : r.nome) },
            { l: 'Meses', f: (r) => r.meses }, { l: 'Valor', f: (r) => brl(r.amount) }, { l: 'Modo', f: (r) => <span data-testid={`finance-payment-mode-${r.id}`}>{r.mode}</span> },
            { l: 'Taxa Livrum', f: (r) => `${brl(r.platform_fee)} (${r.platform_pct}%)` },
            { l: 'Você recebe', f: (r) => brl(r.editor_amount) }, { l: 'Liberado em', f: (r) => fmtDay(r.eligible_at) },
            { l: 'Situação', f: (r) => (r.simulated ? 'Simulação' : r.payout_id ? 'Repassado' : 'A receber') }
          ]} />
      </div>
      <div style={card}>
        <h3 style={{ fontWeight: 600, color: '#2D3748', marginBottom: '0.75rem' }}>Repasses realizados</h3>
        <SimpleTable testId="finance-payouts" rows={data.payouts} empty="Nenhum repasse realizado ainda."
          cols={[{ l: 'Repasse de', f: (r) => fmtDay(r.scheduled_date) }, { l: 'Pago em', f: (r) => fmtDay(r.paid_at) }, { l: 'Assinaturas', f: (r) => r.payments_count }, { l: 'Valor', f: (r) => brl(r.amount) },
            { l: 'Comprovante', f: (r) => (r.receipt
              ? <button type="button" onClick={() => openReceipt(`${API}/digital/finance/payouts/${r.id}/receipt`, headers)} data-testid={`payout-receipt-view-${r.id}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: '#2c7a7b', fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                  <FileText className="w-4 h-4" />Ver PDF</button>
              : <span style={{ color: '#a0aec0' }}>Aguardando</span>) }]} />
      </div>
    </div>
  );
};
